"use client";

/**
 * The Community Forum inside the native shell. Every /m/forum/* route wraps
 * the DESKTOP page/component 1:1 (same code, can't drift — the Rewards /
 * Settings playbook) and this shell supplies what the desktop
 * forum/layout.tsx supplied there: the access guard for deep links and the
 * serif type. Visibility of the Hub tile is decided in MobileBottomNav from
 * the same server verdict (enterprise hierarchy, owner switch, solo switch);
 * this guard is the second line for anyone who lands here by URL.
 */
import { useRouter } from "next/navigation";
import { ShieldOff, Settings2 } from "lucide-react";
import { Button } from "@/app/_module/components/ui/button";
import { Card, CardContent } from "@/app/_module/components/ui/card";
import { Skeleton } from "@/app/_module/components/ui/skeleton";
import { useForumAccess } from "@/app/(authenticated)/forum/_modules/hooks/useForumAccess";

const FORUM_TYPE_CSS = `
.forum-serif{font-family:var(--font-fraunces),Georgia,"Times New Roman",serif;font-variation-settings:"SOFT" 40;letter-spacing:-.005em}
`;

export function ForumShell({ children }: { children: React.ReactNode }) {
  const { eligibility, isLoading, canSee } = useForumAccess();
  const router = useRouter();

  if (isLoading && !eligibility) {
    return (
      <div className="mx-auto w-full m-col space-y-3 px-4 pb-4 pt-3">
        <style>{FORUM_TYPE_CSS}</style>
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-56 w-full" />
      </div>
    );
  }

  if (!canSee) {
    const reason = eligibility?.reason;
    const canEnable =
      !!eligibility?.canToggleAgency && (reason === "FORUM_DISABLED_BY_AGENCY" || reason === "FORUM_OPT_IN_REQUIRED");
    const title =
      reason === "FORUM_DISABLED_BY_AGENCY"
        ? "The Community is off for your agency"
        : reason === "FORUM_OPT_IN_REQUIRED"
          ? "The Community is off for your account"
          : "The Community isn't available on your account";
    const body =
      reason === "FORUM_DISABLED_BY_AGENCY"
        ? canEnable
          ? "Turn it on in Settings and every agent in your agency gets the Forum."
          : "Ask your agency owner to turn on the Community in their settings."
        : reason === "FORUM_OPT_IN_REQUIRED"
          ? "Turn it on in Settings to join the floor — one room for independent InsuraCentral agents."
          : "The Community is the cross-agency forum for independent InsuraCentral agents.";
    return (
      <div className="mx-auto w-full max-w-lg px-4 py-8">
        <style>{FORUM_TYPE_CSS}</style>
        <Card>
          <CardContent className="flex flex-col items-center py-10 text-center">
            <ShieldOff className="h-8 w-8 text-muted-foreground/50" strokeWidth={1.75} />
            <h2 className="forum-serif mt-4 text-[20px] font-medium">{title}</h2>
            <p className="mt-1.5 max-w-sm text-[13px] leading-5 text-muted-foreground">{body}</p>
            {canEnable && (
              <Button
                size="sm"
                className="mt-4 gap-1.5"
                onClick={() => router.push("/m/settings/account?accountTab=preferences&prefTab=general")}
              >
                <Settings2 className="h-3.5 w-3.5" />
                Open Community settings
              </Button>
            )}
            {!canEnable && (
              <Button size="sm" variant="outline" className="mt-4" onClick={() => router.replace("/m")}>
                Back to the app
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full m-col px-3 pb-4 pt-2">
      <style>{FORUM_TYPE_CSS}</style>
      <div className="m-embed m-forum">{children}</div>
    </div>
  );
}
